import axios from 'axios';
import { client } from '@/services/api/axios.ts';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

export type TBMSDeviceStatus = {
  statusModbus: number | null;
  statusError: number | null;
  statusIdle: number | null;
  statusCharge: number | null;
  statusDischarge: number | null;
  stateOfCharge: number | null;
  batteryVoltage: number | null;
  batteryCurrent: number | null;
  batteryTemperature: number | null;
  chargingCycle: number | null;
  dischargingCycle: number | null;
  stateOfHealth: number | null;
  balancingMode: number | null;
  availableCellModules: number | null;
  batteryMinCellVoltage: number;
  batteryMaxCellVoltage: number;
  stringsConnected: number;
  stringsInstalled: number;
};

export type TBMSDeviceModuleStatus = {
  stateOfCharge: number | null;
  moduleVoltage: number | null;
  stringCurrent: number | null;
  temperatureABO: number | null;
  minimumVoltageCell: number | null;
  maximumVoltageCell: number | null;
  minimumStateOfHealth: number | null;
  maximumStateOfHealth: number | null;
  cells: {
    C: number;
    V: number;
    T: number;
    SoH: number;
  }[];
};

async function getBMSStatusDeviceQuery<T extends number | undefined>({
  deviceId,
  moduleNumber,
}: {
  deviceId: string | undefined;
  moduleNumber?: T;
}): Promise<
  (T extends number ? TBMSDeviceModuleStatus : TBMSDeviceStatus) | undefined | never
> {
  if (!deviceId) return undefined;

  const searchParams = new URLSearchParams();
  if (moduleNumber) {
    searchParams.set('moduleNumber', String(moduleNumber));
  }

  try {
    const { data } = await client.get<{
      body: T extends number ? TBMSDeviceModuleStatus : TBMSDeviceStatus;
    }>(`/devices/${deviceId}/status?${searchParams.toString()}`);

    return data.body;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(`${error?.response?.data?.message}`);
    }

    if (error instanceof Error) {
      throw new Error(`${error.message}}`);
    }
  }
}

export const useGetBMSStatusDeviceQuery = <T extends number | undefined>({
  deviceId,
  moduleNumber,
  enabled = true,
}: {
  deviceId: string | undefined;
  moduleNumber?: T;
  enabled?: boolean;
}): UseQueryResult<
  NoInfer<(T extends number ? TBMSDeviceModuleStatus : TBMSDeviceStatus) | undefined>
> => {
  return useQuery({
    queryKey: ['devices', deviceId, 'status', moduleNumber ?? null],
    queryFn: () => getBMSStatusDeviceQuery({ deviceId, moduleNumber }),
    retry: 1,
    staleTime: 1000 * 60,
    enabled,
  });
};
